import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import PageHome from './pages/home';
import PageAcquisition from './pages/acquisition';
import PageCampaignDetail from './pages/campaign-detail';
import PageChannelDetail from './pages/channel-detail';
import PageAffiliates from './pages/affiliates';
import PageInfluencers from './pages/influencers';
import PageOperations from './pages/operations';
import PageOperationsPromotionDetail from './pages/operations-promotion-detail';
// Live outside this folder (config-driven modules area), but mounted here as
// fixed React routes instead of via `ModuleElement` (`oh.module.wallet` /
// `oh.module.ad-budget-limit`).
// import PageWallet from '@/components/modules/pages/wallet';
import PageWallet from '@/nova/pages/wallet';

import PageProduct from './pages/product';
import PageProductDetail from './pages/product-detail';
import PageCreatives from './pages/creatives';
import PageLogos from './pages/logos';
import PageArtStyle from './pages/art-style';
import PagePlayers from './pages/players';
import PageSettings from './pages/settings';
import PageSettingsSection from './pages/settings/section';
import PageMailboxDetail from './pages/mailbox/detail';
import PagePulse from './pages/pulse';
import PageStats from './pages/stats';
import PageBlog from './pages/blog';
import PageBlogEditor from './pages/blog-editor';
import PageBlogPreview from './pages/blog-preview';
import { FeatureRoute } from './components/FeatureRoute';
import PageSeo from './pages/seo';
import PageWebsiteHub from './pages/website';
import PageMarketingHub from './pages/marketing-assets';
import { MovedSectionRedirect, WebsiteIndex } from './pages/website/redirects';
import { WebsiteShell } from './pages/website/components/WebsiteShell';
import PageNotFound from './pages/not-found';
import PageTransition, { BrandTransition } from './transitions';
import { MAILBOX_DETAIL_PATH } from './routes';
import type { LayoutKey } from './layouts';

/**
 * Fixed routes for the nova area — these replace the config-driven
 * `moduleKey` / `ModuleElement` mounting. The dispatchers (campaign / channel /
 * promotion detail) read `:id` via `useParams` themselves.
 *
 * Chrome is HAND-WRITTEN (`../layouts`): every route renders inside the layout
 * named by its `layout` key (default `main` — side menu, top bar, content card,
 * Lumio chat, mobile nav). The old `FixedConfigPage` bridge to the remote
 * config's `page_layout_id` chrome is no longer used here, and the layout owns
 * the auth gate (`/login` redirect) the config pages used to provide.
 */
export const HOME_ROOT = '/account/home';
export const ACQUISITION_ROOT = '/account/marketing';
export const OPERATIONS_ROOT = '/account/operations';
export const PRODUCT_ROOT = '/account/product';
export const WEBSITE_ROOT = '/account/website';
/** The blog area under Website: the list, the editor and the preview all live below it. The same
 *  literal as `ROUTE.blog()`; `pages/blog/newArticles.ts` reads it through the builder. */
export const BLOG_ROOT = `${WEBSITE_ROOT}/blog`;
export const MARKETING_ROOT = '/account/marketing-assets';
export const PLAYERS_ROOT = '/account/players';
export const WALLET_ROOT = '/account/wallet';
export const SETTINGS_ROOT = '/account/settings';
export const PULSE_ROOT = '/account/pulse';
export const STATS_ROOT = '/account/stats';

export type NovaRoute = {
  path: string;
  /**
   * The section root URL this route belongs to. No longer used for chrome —
   * still consumed by `studio-breadcrumb-nav` to resolve which section a deep
   * route highlights.
   */
  areaRoot: string;
  element: React.ReactElement;
  /** Browser-tab title for this page — see `DocumentTitle` below. */
  title: string;
  /**
   * Which hand-written layout wraps this route (`src/nova/layouts`
   * registry key). Omitted → `main`.
   */
  layout?: LayoutKey;
};

/** Renders `page` only while `flag` is on, else the 404 page (a hidden rail entry alone does not hide a
 *  section). Website Creatives (`creatives`), Game Lists (`creatives-games`), Marketing (`creatives-assets`)
 *  and Art Style (`art-style`) are all generally available, their flags kept as rollback switches; SEO
 *  (`website-seo`) is off until its services are live. */
const gated = (
  flag: 'creatives' | 'creatives-games' | 'creatives-assets' | 'art-style' | 'website-seo',
  page: React.ReactElement,
): React.ReactElement => (
  <FeatureRoute flag={flag} fallback={<PageNotFound />}>
    {page}
  </FeatureRoute>
);

/** A Marketing Assets sub-tab's deep links live under Marketing Assets, so they carry its gate
 *  (`creatives-assets`) PLUS the one the page derives that sub-tab from (`creatives-video` for Video Ads,
 *  `creatives-banner-kits`, `creatives-html5-banners`, `creatives-playables`): a URL typed straight in answers Not
 *  Found while either is
 *  off, instead of a page that can never derive the view. Every area route has the same nesting (FeatureRoute >
 *  FeatureRoute > PageCreatives), so React keeps ONE page instance across them. */
const assetGated = (
  flag:
    | 'creatives-video'
    | 'creatives-banner-kits'
    | 'creatives-html5-banners'
    | 'creatives-playables'
    | 'creatives-landing-pages',
  page: React.ReactElement,
): React.ReactElement =>
  gated(
    'creatives-assets',
    <FeatureRoute flag={flag} fallback={<PageNotFound />}>
      {page}
    </FeatureRoute>,
  );

/** The Video Ads deep links (see {@link assetGated}). */
const videosGated = (page: React.ReactElement): React.ReactElement => assetGated('creatives-video', page);
/** A Website area route: its page inside the section's shared frame (one header, the area header with the
 *  `website-hub` flag on and the tab row with it off, the content sliding beneath it). Kept outside any flag gate so
 *  the frame is the same element at the same place on every area, and so stays mounted. */
const websiteTab = (page: React.ReactElement): React.ReactElement => <WebsiteShell>{page}</WebsiteShell>;

const baseRoutes: NovaRoute[] = [
  { path: HOME_ROOT, areaRoot: HOME_ROOT, title: 'Home', element: <PageHome /> },
  { path: ACQUISITION_ROOT, areaRoot: ACQUISITION_ROOT, title: 'Acquisition', element: <PageAcquisition /> },
  { path: '/account/marketing/campaign/:id', areaRoot: ACQUISITION_ROOT, title: 'Campaign', element: <PageCampaignDetail /> },
  { path: '/account/marketing/network/:id', areaRoot: ACQUISITION_ROOT, title: 'Channel', element: <PageChannelDetail /> },
  { path: '/account/marketing/affiliates', areaRoot: ACQUISITION_ROOT, title: 'Affiliates', element: <PageAffiliates /> },
  { path: '/account/marketing/influencers', areaRoot: ACQUISITION_ROOT, title: 'Influencers', element: <PageInfluencers /> },
  { path: OPERATIONS_ROOT, areaRoot: OPERATIONS_ROOT, title: 'Operations', element: <PageOperations /> },
  { path: '/account/operations/promotion/:id', areaRoot: OPERATIONS_ROOT, title: 'Promotion', element: <PageOperationsPromotionDetail /> },
  { path: WALLET_ROOT, areaRoot: WALLET_ROOT, title: 'Wallet', element: <PageWallet /> },
  { path: PRODUCT_ROOT, areaRoot: PRODUCT_ROOT, title: 'Product', element: <PageProduct /> },
  { path: '/account/product/:id', areaRoot: PRODUCT_ROOT, title: 'Product', element: <PageProductDetail /> },
  // Website section (pages/website): the root, then Website Creatives · Logos · Art Style · Game Lists · Blog. The root
  // is the hub with the `website-hub` flag on, and lands on the first available tab with it off.
  { path: WEBSITE_ROOT, areaRoot: WEBSITE_ROOT, title: 'Website', element: <WebsiteIndex hub={<PageWebsiteHub />} /> },
  {
    path: `${WEBSITE_ROOT}/creatives`,
    areaRoot: WEBSITE_ROOT,
    title: 'Website Creatives',
    element: websiteTab(gated('creatives', <PageCreatives section="banners" />)),
  },
  // Ungated, as it was under Settings → Website Settings before it moved here.
  { path: `${WEBSITE_ROOT}/logos`, areaRoot: WEBSITE_ROOT, title: 'Logos', element: websiteTab(<PageLogos />) },
  // Generally available; answers 404 only if the `art-style` rollback switch is turned off.
  {
    path: `${WEBSITE_ROOT}/art-style`,
    areaRoot: WEBSITE_ROOT,
    title: 'Art Style',
    element: websiteTab(gated('art-style', <PageArtStyle />)),
  },
  {
    path: `${WEBSITE_ROOT}/games`,
    areaRoot: WEBSITE_ROOT,
    title: 'Game Lists',
    element: websiteTab(gated('creatives-games', <PageCreatives section="games" />)),
  },
  // Website → SEO: the owner's meta and footer text per route, behind `website-seo` (off until its services are live).
  { path: `${WEBSITE_ROOT}/seo`, areaRoot: WEBSITE_ROOT, title: 'SEO', element: websiteTab(gated('website-seo', <PageSeo />)) },
  // Marketing section: Marketing Assets (generally available). The root is the hub, one card per area; each
  // area is its library route below, headed by a back button to the hub. The area routes share one element shape, so
  // React keeps ONE PageCreatives across them (a library and its editors), while the hub is a page of its own.
  {
    path: MARKETING_ROOT,
    areaRoot: MARKETING_ROOT,
    title: 'Marketing',
    element: gated('creatives-assets', <PageMarketingHub />),
  },
  // Marketing Assets > Video Ads deep links: the library, a new ad, and an ad by id.
  { path: `${MARKETING_ROOT}/videos`, areaRoot: MARKETING_ROOT, title: 'Video ads', element: videosGated(<PageCreatives section="custom" />) },
  {
    path: `${MARKETING_ROOT}/videos/new`,
    areaRoot: MARKETING_ROOT,
    title: 'New video ad',
    element: videosGated(<PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/videos/:id`,
    areaRoot: MARKETING_ROOT,
    title: 'Edit video ad',
    element: videosGated(<PageCreatives section="custom" />),
  },
  // Marketing Assets > Video Ads v2.0 (single clip) deep links, same shape and same gates as Video Ads above.
  {
    path: `${MARKETING_ROOT}/videos-v2`,
    areaRoot: MARKETING_ROOT,
    title: 'Video ads v2.0',
    element: videosGated(<PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/videos-v2/new`,
    areaRoot: MARKETING_ROOT,
    title: 'New video ad v2.0',
    element: videosGated(<PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/videos-v2/:id`,
    areaRoot: MARKETING_ROOT,
    title: 'Edit video ad v2.0',
    element: videosGated(<PageCreatives section="custom" />),
  },
  // Marketing Assets > Image Banners (banner kits) deep links: the library, a new kit, and a kit by id.
  {
    path: `${MARKETING_ROOT}/banners`,
    areaRoot: MARKETING_ROOT,
    title: 'Image banners',
    element: assetGated('creatives-banner-kits', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/banners/new`,
    areaRoot: MARKETING_ROOT,
    title: 'New image banner',
    element: assetGated('creatives-banner-kits', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/banners/:id`,
    areaRoot: MARKETING_ROOT,
    title: 'Edit image banner',
    element: assetGated('creatives-banner-kits', <PageCreatives section="custom" />),
  },
  // Marketing Assets > HTML5 Banners deep links: the library, a new HTML5 banner, and one by id.
  {
    path: `${MARKETING_ROOT}/html5-banners`,
    areaRoot: MARKETING_ROOT,
    title: 'HTML5 banners',
    element: assetGated('creatives-html5-banners', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/html5-banners/new`,
    areaRoot: MARKETING_ROOT,
    title: 'New HTML5 banner',
    element: assetGated('creatives-html5-banners', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/html5-banners/:id`,
    areaRoot: MARKETING_ROOT,
    title: 'Edit HTML5 banner',
    element: assetGated('creatives-html5-banners', <PageCreatives section="custom" />),
  },
  // Marketing Assets > Mini-games deep links: the library, a new mini-game, and one by id.
  {
    path: `${MARKETING_ROOT}/mini-games`,
    areaRoot: MARKETING_ROOT,
    title: 'Mini-games',
    element: assetGated('creatives-playables', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/mini-games/new`,
    areaRoot: MARKETING_ROOT,
    title: 'New mini-game',
    element: assetGated('creatives-playables', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/mini-games/:id`,
    areaRoot: MARKETING_ROOT,
    title: 'Edit mini-game',
    element: assetGated('creatives-playables', <PageCreatives section="custom" />),
  },
  // Marketing Assets > Landing Pages: the saved and live pages, a new one, and a saved one by id.
  {
    path: `${MARKETING_ROOT}/landing-pages`,
    areaRoot: MARKETING_ROOT,
    title: 'Landing pages',
    element: assetGated('creatives-landing-pages', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/landing-pages/new`,
    areaRoot: MARKETING_ROOT,
    title: 'New landing page',
    element: assetGated('creatives-landing-pages', <PageCreatives section="custom" />),
  },
  {
    path: `${MARKETING_ROOT}/landing-pages/:id`,
    areaRoot: MARKETING_ROOT,
    title: 'Edit landing page',
    element: assetGated('creatives-landing-pages', <PageCreatives section="custom" />),
  },
  { path: PLAYERS_ROOT, areaRoot: PLAYERS_ROOT, title: 'Players', element: <PagePlayers /> },
  { path: SETTINGS_ROOT, areaRoot: SETTINGS_ROOT, title: 'Settings', element: <PageSettings /> },
  // Each Settings entry (Website Settings / Profile / Billing / Account /
  // Communications) is its own route — the dispatcher reads `:section` via
  // `useParams`. Website (brand) settings live here now (moved off the standalone
  // `/account/casino-settings`).
  // One mailbox's Server Settings page in the Mailbox section. Listed
  // ABOVE the `:section` dispatcher so the more specific path wins; the page gates itself on the
  // `mailbox` flag and on the address, exactly as the section does.
  { path: MAILBOX_DETAIL_PATH, areaRoot: SETTINGS_ROOT, title: 'Settings', element: <PageMailboxDetail /> },
  { path: '/account/settings/:section', areaRoot: SETTINGS_ROOT, title: 'Settings', element: <PageSettingsSection /> },
  { path: PULSE_ROOT, areaRoot: PULSE_ROOT, title: 'Pulse', element: <PagePulse /> },
  { path: STATS_ROOT, areaRoot: STATS_ROOT, title: 'Stats', element: <PageStats /> },
  { path: `${STATS_ROOT}/website`, areaRoot: STATS_ROOT, title: 'Stats', element: <PageStats /> },
  // Website → Blog authoring, all four pages. Ungated since SB-239: the section is
  // finished, it talks to a real service, and the casino blog is announced.
  { path: BLOG_ROOT, areaRoot: WEBSITE_ROOT, title: 'Blog', element: websiteTab(<PageBlog />) },
  { path: `${BLOG_ROOT}/new`, areaRoot: WEBSITE_ROOT, title: 'New article', element: <PageBlogEditor /> },
  {
    path: `${BLOG_ROOT}/:id/edit`,
    areaRoot: WEBSITE_ROOT,
    title: 'Edit article',
    element: <PageBlogEditor />,
  },
  {
    path: `${BLOG_ROOT}/:id/preview`,
    areaRoot: WEBSITE_ROOT,
    title: 'Article preview',
    element: <PageBlogPreview />,
  },
  // The old section addresses, moved under Website (see MovedSectionRedirect). Video Ads went to Marketing
  // Assets instead, so its old links get their own, more specific route ahead of the generic one.
  {
    path: '/account/creatives/videos/*',
    areaRoot: MARKETING_ROOT,
    title: 'Video ads',
    element: <MovedSectionRedirect from="/account/creatives/videos" to={`${MARKETING_ROOT}/videos`} />,
  },
  {
    path: '/account/creatives/*',
    areaRoot: WEBSITE_ROOT,
    // To the section ROOT (the hub, or with the `website-hub` flag off the first available tab), not the creatives
    // area: an old bookmark still lands on content when the `creatives` rollback switch is off, instead of 404ing on a
    // gated area.
    title: 'Website Creatives',
    element: <MovedSectionRedirect from="/account/creatives" to={WEBSITE_ROOT} />,
  },
  {
    path: '/account/blog/*',
    areaRoot: WEBSITE_ROOT,
    title: 'Blog',
    element: <MovedSectionRedirect from="/account/blog" to={`${WEBSITE_ROOT}/blog`} />,
  },
  // Unconditional global 404. `fixedRoutes` is spread before the config-driven
  // `routes` in routes.tsx, so this '*' always wins the routing tie against any
  // tenant config that defines its own global `setPages['*']` page — that's
  // intentional: this NotFound supersedes the old config-driven global-page
  // mechanism, it doesn't merely gap-fill around it. MUST stay LAST in this
  // array: studio-breadcrumb-nav's sectionRootFor() walks novaRoutes in order
  // and returns the first match, and '*' matches every path — placed earlier
  // it would shadow every other route's section highlight.
  { path: '*', areaRoot: '', title: 'Page not found', element: <PageNotFound /> },
];

/**
 * Sets the browser-tab <title> for a fixed route.
 *
 * These nova routes render outside the config engine (wrapped in a hand-written
 * `layouts/` layout, not `<Page>`), so no `<PageMetaTags>` mounts here — this is the
 * SOLE title writer on a nova route, so it sets `document.title` imperatively rather
 * than through react-helmet (whose deferred, module-global rAF + multi-instance
 * reduction lagged a full step behind the page content).
 *
 * The title is driven off i18next's `languageChanged` event DIRECTLY, not off a
 * `useTranslation()` re-render. That decoupling matters: sibling routes can share the
 * same `title` (e.g. `/account/settings` and `/account/settings/:section` are both
 * "Settings"), and reconciling between them doesn't re-run a render-gated effect — so a
 * language switch on the section page left the tab stale until you hit a differently
 * titled route. Subscribing to the i18n event rewrites the title the moment the
 * language changes, with the LIVE-language value (`i18n.t`, never a stale render-bound
 * `t`), regardless of whether/when this component re-renders. Copy is translated
 * (English-as-key, per the area convention for non-`<Text>` strings); `TITLE_PREFIX` is
 * a literal.
 *
 * The blank-then-set in `apply` works around a Chrome tab-title bug, confirmed by inspecting
 * the DOM (the `<title>` element was already correct; only the visible tab lagged). On a
 * same-document history POP (the drilldown `BackHeader`'s `navigate(-1)`) Chrome restores the
 * tab title from its per-history-entry snapshot — the language that entry held when you last
 * left it. Sibling routes can share a title (`/account/settings` and `/account/settings/:section`
 * are both "Settings"), so `document.title` is ALREADY the correct string on arrival; a plain
 * re-assign is a no-op, fires no title-change, and Chrome keeps the stale snapshot on the tab.
 * Blanking first guarantees a real mutation the browser honours, refreshing the tab. Keyed on
 * `location.key` so it re-fires on every navigation (including a POP to a same-titled sibling,
 * where `title` alone doesn't change); the deferred call re-asserts after the traversal settles.
 */
const TITLE_PREFIX = 'Owner Studio - ';

const DocumentTitle = ({ title }: { title: string }): null => {
  const { i18n } = useTranslation();
  const location = useLocation();
  React.useEffect(() => {
    const apply = (): void => {
      const next = `${TITLE_PREFIX}${i18n.t(title)}`;
      if (document.title === next) document.title = ''; // force a real change (see above)
      document.title = next;
    };
    apply(); // mount + route change (title prop) + every navigation (location.key)
    const deferred = window.setTimeout(apply, 0); // re-assert after the POP traversal settles
    i18n.on('languageChanged', apply); // every language switch, render-independent
    return () => {
      window.clearTimeout(deferred);
      i18n.off('languageChanged', apply);
    };
  }, [title, i18n, location.key]);
  return null;
};

/**
 * Each page is wrapped in `PageTransition` so switching views / drilling in and
 * out slides horizontally (forward → in from the right, back → in from the left).
 * The wrapper reconciles in place across these routes (they share one layout
 * chrome), which is what lets it animate the outgoing page out as the next one
 * comes in. See `transitions/PageTransition.tsx`.
 *
 * `BrandTransition` sits *inside* `PageTransition`, wrapping the page itself, so
 * the two stay independent: navigation slides (URL changes, brand doesn't), and a
 * brand switch cross-page-fades (brand changes, URL doesn't). It fades the whole
 * view out and back in whenever the active brand changes — see
 * `transitions/BrandTransition.tsx`.
 */
export const novaRoutes: NovaRoute[] = baseRoutes.map((route) => ({
  ...route,
  element: (
    <React.Fragment>
      <DocumentTitle title={route.title} />
      <PageTransition>
        <BrandTransition>{route.element}</BrandTransition>
      </PageTransition>
    </React.Fragment>
  ),
}));

export default novaRoutes;
